import { useRef, useEffect } from "react";
import * as THREE from "three";

/**
 * 3D Altelix NEMA Enclosure with AI Agent Squad inside.
 * Adapted from the proposal's showcase-3d.js.
 * The door auto-opens to reveal the agents and the login form.
 */
export function AgentNetwork3D() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // -- Scene --
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x080c16);
    scene.fog = new THREE.FogExp2(0x080c16, 0.04);

    // -- Camera --
    const camera = new THREE.PerspectiveCamera(
      40, container.clientWidth / container.clientHeight, 0.1, 100
    );
    camera.position.set(4, 3, 6);
    camera.lookAt(0, 0, 0);

    // -- Renderer --
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    container.appendChild(renderer.domElement);

    // -- Ground + Grid --
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(30, 30),
      new THREE.MeshStandardMaterial({ color: 0x0c1018, roughness: 0.85, metalness: 0.1 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.6;
    ground.receiveShadow = true;
    scene.add(ground);

    const grid = new THREE.GridHelper(20, 40, 0x141428, 0x141428);
    grid.position.y = -1.59;
    (grid.material as THREE.Material).opacity = 0.25;
    (grid.material as THREE.Material).transparent = true;
    scene.add(grid);

    // -- Lighting (proposal palette: purple fill, teal rim) --
    scene.add(new THREE.AmbientLight(0x404060, 0.4));

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.9);
    keyLight.position.set(5, 8, 4);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.camera.near = 0.5;
    keyLight.shadow.camera.far = 25;
    keyLight.shadow.camera.left = -5;
    keyLight.shadow.camera.right = 5;
    keyLight.shadow.camera.top = 5;
    keyLight.shadow.camera.bottom = -5;
    keyLight.shadow.bias = -0.0001;
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x6c5ce7, 0.3);
    fillLight.position.set(-4, 3, -2);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0x00cec9, 0.2);
    rimLight.position.set(-2, 5, -5);
    scene.add(rimLight);

    scene.add(new THREE.PointLight(0x334466, 0.3, 10).translateY(-1).translateZ(2));

    // =====================================================
    // NEMA Enclosure (from proposal showcase-3d.js)
    // =====================================================
    const W = 2.2, H = 2.6, D = 1.2, WALL = 0.08, LID = 0.1;

    const mt = {
      outer: new THREE.MeshStandardMaterial({ color: 0x9ea3a8, roughness: 0.55, metalness: 0.15 }),
      inner: new THREE.MeshStandardMaterial({ color: 0xd8dce0, roughness: 0.7, metalness: 0.05 }),
      edge:  new THREE.MeshStandardMaterial({ color: 0x888d92, roughness: 0.4, metalness: 0.25 }),
      hinge: new THREE.MeshStandardMaterial({ color: 0xb0b0b0, roughness: 0.3, metalness: 0.7 }),
      latch: new THREE.MeshStandardMaterial({ color: 0xc0c0c0, roughness: 0.25, metalness: 0.8 }),
      seal:  new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.9, metalness: 0.0 }),
      label: new THREE.MeshStandardMaterial({ color: 0xee2222, roughness: 0.6, metalness: 0.0 }),
      white: new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.7, metalness: 0.0 }),
    };

    const boxGroup = new THREE.Group();
    scene.add(boxGroup);

    // -- Body (back shell with walls, seal, mounting bosses) --
    function buildBody() {
      const g = new THREE.Group();
      // Back wall
      const back = new THREE.Mesh(new THREE.BoxGeometry(W, H, WALL), mt.outer);
      back.position.z = -D / 2; back.castShadow = true; g.add(back);
      // Back inner face
      const bi = new THREE.Mesh(new THREE.BoxGeometry(W - WALL * 2, H - WALL * 2, 0.01), mt.inner);
      bi.position.z = -D / 2 + WALL / 2 + 0.01; g.add(bi);
      // Top & bottom walls
      const tbGeo = new THREE.BoxGeometry(W, WALL, D);
      const topW = new THREE.Mesh(tbGeo, mt.outer); topW.position.y = H / 2 - WALL / 2; topW.castShadow = true; g.add(topW);
      const botW = new THREE.Mesh(tbGeo, mt.outer); botW.position.y = -H / 2 + WALL / 2; botW.castShadow = true; g.add(botW);
      // Left & right walls
      const sGeo = new THREE.BoxGeometry(WALL, H, D);
      const leftW = new THREE.Mesh(sGeo, mt.outer); leftW.position.x = -W / 2 + WALL / 2; leftW.castShadow = true; g.add(leftW);
      const rightW = new THREE.Mesh(sGeo, mt.outer); rightW.position.x = W / 2 - WALL / 2; rightW.castShadow = true; g.add(rightW);
      // Inner side walls
      const isGeo = new THREE.BoxGeometry(0.01, H - WALL * 2, D - WALL);
      const il = new THREE.Mesh(isGeo, mt.inner); il.position.set(-W / 2 + WALL + 0.01, 0, WALL / 2); g.add(il);
      const ir = new THREE.Mesh(isGeo, mt.inner); ir.position.set(W / 2 - WALL - 0.01, 0, WALL / 2); g.add(ir);
      // Inner top/bottom
      const itGeo = new THREE.BoxGeometry(W - WALL * 2, 0.01, D - WALL);
      const iTop = new THREE.Mesh(itGeo, mt.inner); iTop.position.set(0, H / 2 - WALL - 0.01, WALL / 2); g.add(iTop);
      const iBot = new THREE.Mesh(itGeo, mt.inner); iBot.position.set(0, -H / 2 + WALL + 0.01, WALL / 2); g.add(iBot);
      // Rubber seal
      const sw = 0.03, sd = 0.02;
      const sealHGeo = new THREE.BoxGeometry(W - WALL * 2, sw, sd);
      const sealT = new THREE.Mesh(sealHGeo, mt.seal); sealT.position.set(0, H / 2 - WALL, D / 2 - sd / 2); g.add(sealT);
      const sealB = new THREE.Mesh(sealHGeo, mt.seal); sealB.position.set(0, -H / 2 + WALL, D / 2 - sd / 2); g.add(sealB);
      const sealVGeo = new THREE.BoxGeometry(sw, H - WALL * 2, sd);
      const sealL = new THREE.Mesh(sealVGeo, mt.seal); sealL.position.set(-W / 2 + WALL, 0, D / 2 - sd / 2); g.add(sealL);
      const sealR = new THREE.Mesh(sealVGeo, mt.seal); sealR.position.set(W / 2 - WALL, 0, D / 2 - sd / 2); g.add(sealR);
      // Mounting bosses
      const bGeo = new THREE.CylinderGeometry(0.06, 0.06, 0.3, 8);
      [[-W / 2 + 0.25, H / 2 - 0.25], [W / 2 - 0.25, H / 2 - 0.25],
       [-W / 2 + 0.25, -H / 2 + 0.25], [W / 2 - 0.25, -H / 2 + 0.25]].forEach(p => {
        const boss = new THREE.Mesh(bGeo, mt.inner);
        boss.rotation.x = Math.PI / 2;
        boss.position.set(p[0], p[1], -D / 2 + WALL + 0.15);
        g.add(boss);
      });
      return g;
    }

    // -- Door / Lid --
    function buildDoor() {
      const pivot = new THREE.Group();
      pivot.position.set(-W / 2, 0, D / 2);
      const door = new THREE.Group();
      // Front panel
      const front = new THREE.Mesh(new THREE.BoxGeometry(W, H, LID), mt.outer);
      front.castShadow = true; front.receiveShadow = true; door.add(front);
      // Inner face
      const inf = new THREE.Mesh(new THREE.BoxGeometry(W - 0.15, H - 0.15, 0.01), mt.inner);
      inf.position.z = -LID / 2 - 0.005; door.add(inf);
      // Frame edges
      const ft = 0.04, fd = 0.02;
      const fhGeo = new THREE.BoxGeometry(W + 0.04, ft, LID + fd);
      const fTop = new THREE.Mesh(fhGeo, mt.edge); fTop.position.y = H / 2; fTop.castShadow = true; door.add(fTop);
      const fBot = new THREE.Mesh(fhGeo, mt.edge); fBot.position.y = -H / 2; fBot.castShadow = true; door.add(fBot);
      const fsGeo = new THREE.BoxGeometry(ft, H + ft * 2, LID + fd);
      const fL = new THREE.Mesh(fsGeo, mt.edge); fL.position.x = -W / 2; fL.castShadow = true; door.add(fL);
      const fR = new THREE.Mesh(fsGeo, mt.edge); fR.position.x = W / 2; fR.castShadow = true; door.add(fR);
      // Warning label
      const lBg = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.55, 0.005), mt.white);
      lBg.position.set(0.15, 0.15, LID / 2 + 0.003); door.add(lBg);
      const lHd = new THREE.Mesh(new THREE.BoxGeometry(0.65, 0.12, 0.006), mt.label);
      lHd.position.set(0.15, 0.35, LID / 2 + 0.006); door.add(lHd);
      // Altelix logo placeholder
      const logoMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(0.6, 0.2),
        new THREE.MeshStandardMaterial({ color: 0x1a3a6e, roughness: 0.5, transparent: true })
      );
      logoMesh.position.set(0, -H / 2 + 0.22, LID / 2 + 0.006);
      door.add(logoMesh);
      // Latch
      const lBody = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.2, 0.15), mt.latch);
      lBody.position.set(W / 2 + 0.02, 0, 0); door.add(lBody);
      const lKnob = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.1, 8), mt.latch);
      lKnob.rotation.z = Math.PI / 2;
      lKnob.position.set(W / 2 + 0.08, 0, 0); door.add(lKnob);
      // Offset door so pivot at left edge
      door.position.x = W / 2;
      pivot.add(door);
      return pivot;
    }

    // -- Hinges --
    function buildHinges() {
      const g = new THREE.Group();
      [H / 2 - 0.4, 0, -H / 2 + 0.4].forEach(y => {
        const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.18, 12), mt.hinge);
        barrel.position.set(-W / 2, y, D / 2); barrel.castShadow = true; g.add(barrel);
        const pGeo = new THREE.BoxGeometry(0.12, 0.15, 0.02);
        const p1 = new THREE.Mesh(pGeo, mt.hinge); p1.position.set(-W / 2 + 0.06, y, D / 2 - 0.04); g.add(p1);
        const p2 = new THREE.Mesh(pGeo, mt.hinge); p2.position.set(-W / 2 + 0.06, y, D / 2 + 0.04); g.add(p2);
      });
      return g;
    }

    // -- Assemble enclosure --
    boxGroup.add(buildBody());
    const doorPivot = buildDoor();
    boxGroup.add(doorPivot);
    boxGroup.add(buildHinges());
    boxGroup.position.y = 0.1;

    // =====================================================
    // AI Agent Squad (holographic nodes inside enclosure)
    // =====================================================
    type AgentState = {
      group: THREE.Group; wire: THREE.Mesh; core: THREE.Mesh;
      ring: THREE.Mesh; sat: THREE.Mesh; baseY: number; phase: number; orbitR: number;
    };
    const agents: AgentState[] = [];
    const agentLines: THREE.Line[] = [];

    const squadGroup = new THREE.Group();
    const cfg = [
      { color: 0x6c5ce7, pos: [0, 0.50, -0.15],  geo: "octa" },
      { color: 0x00cec9, pos: [0.42, 0, -0.15],   geo: "box" },
      { color: 0xfdcb6e, pos: [-0.42, 0, -0.15],  geo: "ico" },
      { color: 0x55efc4, pos: [0, -0.50, -0.15],  geo: "dodeca" },
    ];

    cfg.forEach((c, i) => {
      const node = new THREE.Group();
      node.position.set(c.pos[0], c.pos[1], c.pos[2]);
      let outerGeo: THREE.BufferGeometry;
      switch (c.geo) {
        case "octa":   outerGeo = new THREE.OctahedronGeometry(0.15, 0); break;
        case "box":    outerGeo = new THREE.BoxGeometry(0.22, 0.22, 0.22); break;
        case "ico":    outerGeo = new THREE.IcosahedronGeometry(0.15, 1); break;
        default:       outerGeo = new THREE.DodecahedronGeometry(0.15, 0); break;
      }
      const wire = new THREE.Mesh(outerGeo, new THREE.MeshStandardMaterial({
        color: c.color, emissive: c.color, emissiveIntensity: 0.35,
        wireframe: true, transparent: true, opacity: 0.75,
      }));
      node.add(wire);
      const core = new THREE.Mesh(new THREE.SphereGeometry(0.045, 12, 12),
        new THREE.MeshStandardMaterial({
          color: 0xffffff, emissive: c.color, emissiveIntensity: 0.9,
          transparent: true, opacity: 0.9,
        })
      );
      node.add(core);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.005, 8, 48),
        new THREE.MeshStandardMaterial({
          color: c.color, emissive: c.color, emissiveIntensity: 0.25,
          transparent: true, opacity: 0.45,
        })
      );
      ring.rotation.x = Math.PI / 3 + i * 0.4;
      node.add(ring);
      const sat = new THREE.Mesh(new THREE.SphereGeometry(0.018, 8, 8),
        new THREE.MeshStandardMaterial({ color: c.color, emissive: c.color, emissiveIntensity: 0.7 })
      );
      node.add(sat);
      node.add(new THREE.PointLight(c.color, 0.15, 0.9));
      squadGroup.add(node);
      agents.push({ group: node, wire, core, ring, sat, baseY: c.pos[1], phase: i * Math.PI / 2, orbitR: 0.24 });
    });

    // Connection lines
    const pts = cfg.map(c => new THREE.Vector3(c.pos[0], c.pos[1], c.pos[2]));
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const geo = new THREE.BufferGeometry().setFromPoints([pts[i], pts[j]]);
        const line = new THREE.Line(geo, new THREE.LineBasicMaterial({
          color: 0x6c5ce7, transparent: true, opacity: 0.10,
        }));
        squadGroup.add(line);
        agentLines.push(line);
      }
    }

    // Central hub
    const hubMesh = new THREE.Mesh(new THREE.SphereGeometry(0.03, 16, 16),
      new THREE.MeshStandardMaterial({
        color: 0xffffff, emissive: 0xa29bfe, emissiveIntensity: 0.7,
        transparent: true, opacity: 0.85,
      })
    );
    hubMesh.position.set(0, 0, -0.15);
    squadGroup.add(hubMesh);

    // Data particles
    const particleCount = 30;
    const pPositions = new Float32Array(particleCount * 3);
    for (let k = 0; k < particleCount; k++) {
      pPositions[k * 3]     = (Math.random() - 0.5) * 1.6;
      pPositions[k * 3 + 1] = (Math.random() - 0.5) * 2.0;
      pPositions[k * 3 + 2] = -0.5 + Math.random() * 0.8;
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute("position", new THREE.BufferAttribute(pPositions, 3));
    squadGroup.add(new THREE.Points(pGeo, new THREE.PointsMaterial({
      color: 0xa29bfe, size: 0.02, transparent: true, opacity: 0.35,
      blending: THREE.AdditiveBlending, depthWrite: false,
    })));

    boxGroup.add(squadGroup);

    // =====================================================
    // Animation
    // =====================================================
    let doorAngle = 0;
    let targetAngle = 120;
    let animDir = 1;
    const orbit = { theta: 0.5, phi: 0.8 };
    const radius = 7;
    let dragging = false;
    let prev = { x: 0, y: 0 };

    // Mouse orbit
    const onDown = (e: MouseEvent) => { dragging = true; prev = { x: e.clientX, y: e.clientY }; };
    const onMove = (e: MouseEvent) => {
      if (!dragging) return;
      orbit.theta -= (e.clientX - prev.x) * 0.005;
      orbit.phi = Math.max(0.2, Math.min(Math.PI - 0.2, orbit.phi - (e.clientY - prev.y) * 0.005));
      prev = { x: e.clientX, y: e.clientY };
    };
    const onUp = () => { dragging = false; };
    // Touch
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) { dragging = true; prev = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!dragging || e.touches.length !== 1) return;
      orbit.theta -= (e.touches[0].clientX - prev.x) * 0.005;
      orbit.phi = Math.max(0.2, Math.min(Math.PI - 0.2, orbit.phi - (e.touches[0].clientY - prev.y) * 0.005));
      prev = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onTouchEnd = () => { dragging = false; };

    container.addEventListener("mousedown", onDown);
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseup", onUp);
    container.addEventListener("mouseleave", onUp);
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    container.addEventListener("touchend", onTouchEnd);

    let animId: number;
    function tick() {
      animId = requestAnimationFrame(tick);

      // Door interpolation
      const diff = targetAngle - doorAngle;
      doorAngle += diff * 0.06;
      if (Math.abs(diff) < 0.1) doorAngle = targetAngle;

      // Loop open ↔ close
      if (Math.abs(doorAngle - targetAngle) < 0.5) {
        if (animDir === 1) { targetAngle = 0; animDir = -1; }
        else { targetAngle = 120; animDir = 1; }
      }

      doorPivot.rotation.y = -THREE.MathUtils.degToRad(doorAngle);

      // Animate agents
      const t = performance.now() * 0.001;
      agents.forEach(a => {
        a.group.position.y = a.baseY + Math.sin(t * 1.2 + a.phase) * 0.045;
        a.wire.rotation.x += 0.004;
        a.wire.rotation.y += 0.007;
        a.core.rotation.y -= 0.012;
        a.ring.rotation.z += 0.009;
        const st = t * 2.2 + a.phase;
        a.sat.position.x = Math.cos(st) * a.orbitR;
        a.sat.position.z = Math.sin(st) * a.orbitR * 0.5;
        a.sat.position.y = Math.sin(st * 0.7) * 0.06;
        const pulse = 0.35 + Math.sin(t * 2 + a.phase) * 0.15;
        (a.wire.material as THREE.MeshStandardMaterial).emissiveIntensity = pulse;
        (a.core.material as THREE.MeshStandardMaterial).emissiveIntensity = pulse + 0.45;
      });
      agentLines.forEach((l, i) => {
        (l.material as THREE.LineBasicMaterial).opacity = 0.07 + Math.sin(t * 1.5 + i * 0.9) * 0.06;
      });
      (hubMesh.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5 + Math.sin(t * 3) * 0.25;

      // Auto-rotate
      if (!dragging) orbit.theta += 0.0008;

      // Camera
      camera.position.x = radius * Math.sin(orbit.phi) * Math.cos(orbit.theta);
      camera.position.y = radius * Math.cos(orbit.phi);
      camera.position.z = radius * Math.sin(orbit.phi) * Math.sin(orbit.theta);
      camera.lookAt(0, 0.1, 0);

      renderer.render(scene, camera);
    }
    tick();

    // Resize
    const onResize = () => {
      if (!container.clientWidth || !container.clientHeight) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      container.removeEventListener("mousedown", onDown);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseup", onUp);
      container.removeEventListener("mouseleave", onUp);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ position: "absolute", inset: 0, zIndex: 0, cursor: "grab" }}
    />
  );
}
